<template>
  <div v-if="isVisible" class="financial-section">
    <div class="section-divider"></div>

    <SidebarAccordion title="Situation financière" :default-open="false" v-bind="$attrs">
      <template #summary>
        <span v-if="attentionTitle" class="financial-alert" :title="attentionTitle"><span class="fas fa-circle-exclamation"></span></span>
      </template>

      <div class="summary-row">
        <span>Demandé</span>
        <span class="summary-value">{{ formatAmount(summary.requested) }}</span>
      </div>
      <div v-if="hasPending" class="summary-row">
        <span>Total théorique</span>
        <span class="summary-value">{{ formatAmount(summary.theoretical) }}</span>
      </div>
      <div class="summary-row">
        <span>Encaissé</span>
        <span class="summary-value">{{ formatAmount(summary.collected) }}</span>
      </div>

      <div class="summary-row" :class="{ 'is-provisional': hasPending }">
        <span>Solde</span>
        <span class="summary-value">{{ formatSignedAmount(summary.balance) }}</span>
      </div>
      <p v-if="!hasPending" class="summary-caption">{{ balanceCaption(summary.balance) }}</p>

      <template v-if="hasPending">
        <div class="summary-row">
          <span>Solde théorique</span>
          <span class="summary-value">{{ formatSignedAmount(summary.theoreticalBalance) }}</span>
        </div>
        <p class="summary-caption">{{ balanceCaption(summary.theoreticalBalance) }}</p>
        <p class="summary-note">
          <span class="note-icon"><span class="fas fa-circle-exclamation"></span></span>
          <span>{{ pendingNote }}</span>
        </p>
      </template>

      <p v-for="note in dueNotes" :key="note" class="summary-note">
        <span class="note-icon"><span class="fas fa-circle-exclamation"></span></span>
        <span>{{ note }}</span>
      </p>

      <button type="button" class="section-label collections-label collections-toggle" :disabled="!sortedCollections.length" @click="isCollectionsOpen = !isCollectionsOpen">
        <font-awesome-icon v-if="sortedCollections.length" :icon="isCollectionsOpen ? 'chevron-down' : 'chevron-right'" />
        <span>Encaissements ({{ sortedCollections.length }})</span>
      </button>
      <p v-if="!sortedCollections.length" class="summary-caption">Aucun encaissement enregistré</p>
      <ul v-else-if="isCollectionsOpen" class="collection-list">
        <li v-for="(collection, index) in sortedCollections" :key="collection.id || collection._id || index" class="collection-item" :class="{editable: !readOnly}" :title="readOnly ? undefined : 'Modifier l\'encaissement'" @click="openEditCollectionModal(collection)">
          <div class="collection-main">
            <span class="collection-date">{{ formatDate(collection.date) }}</span>
            <span class="collection-amount">{{ formatAmount(collection.amount) }}</span>
          </div>
          <span class="collection-meta">{{ convertPaymentType(collection.paymentType) }} · {{ purposeLabel(collection) }}</span>
        </li>
      </ul>

      <div v-if="!readOnly" class="add-collection-button" :class="{'is-highlighted': highlightAddCollection}">
        <CustomButton @click="openAddCollectionModal" :is-secondary="!highlightAddCollection" :isCompact="true" :params="buttons.addCollection" />
      </div>
    </SidebarAccordion>
  </div>
  <AddCollectionModal ref="addCollectionModal" :callback="onCollectionAdded" />
  <EditCollectionModal ref="editCollectionModal" :callback="onCollectionAdded" />
</template>

<script>
import {library} from '@fortawesome/fontawesome-svg-core';
import {faCircleExclamation, faChevronDown, faChevronRight} from "@fortawesome/free-solid-svg-icons";
import {BUSINESS_DOCUMENT_TYPE_ENUM} from "@/models/businessDocumentTypeEnum";
import SidebarAccordion from "@/components/businessEdition/SidebarAccordion.vue";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import AddCollectionModal from "@/components/modals/AddCollectionModal.vue";
import EditCollectionModal from "@/components/modals/EditCollectionModal.vue";
import {CollectionController} from "@/controllers/business/CollectionController";
import {PAYMENT_PURPOSE} from "@/models/paymentPurpose";
import {isDepositAborted} from "@/models/depositStatusEnum";
import {convertPaymentType, formattedDate, getBusinessFinancialSummary} from "@/utils";

library.add(faCircleExclamation, faChevronDown, faChevronRight);

export default {
  name: 'FinancialSummarySection',
  components: {SidebarAccordion, CustomButton, AddCollectionModal, EditCollectionModal},
  // Les attributs non déclarés (open, toggle-callback...) sont transmis à l'accordéon pour pouvoir piloter son état depuis le parent
  inheritAttrs: false,
  props: {
    business: {type: Object, default: undefined},
    callback: {type: Function, default: undefined},
    readOnly: {type: Boolean, default: false},
    // Document en attente de règlement : l'encaissement est la prochaine action attendue, le bouton passe en citron pour attirer l'œil
    highlightAddCollection: {type: Boolean, default: false},
  },
  data() {
    return {
      buttons: {
        addCollection: {text: "Ajouter un encaissement"},
      },
      collections: [],
      isLoaded: false,
      // Liste repliée par défaut : seule la synthèse chiffrée est toujours visible (gain de place dans la colonne)
      isCollectionsOpen: false,
    };
  },
  computed: {
    summary() {
      return getBusinessFinancialSummary(this.business, this.collections);
    },
    // Écart entre ce qui est demandé (documents émis) et le total théorique du contrat
    hasPending() {
      return Math.abs(this.summary.pending) >= 0.01;
    },
    pendingNote() {
      return this.summary.pending > 0
          ? this.formatAmount(this.summary.pending) + " restent à demander (document non émis) : le solde ci-dessus est provisoire."
          : "Les documents émis dépassent le total théorique du contrat de " + this.formatAmount(Math.abs(this.summary.pending)) + ".";
    },
    // Factures émises (acompte, puis facture) pas encore intégralement réglées
    dueNotes() {
      const notes = [];
      const remainingOf = type => {
        const document = this.summary.documents.find(d => d.type === type);
        return document && document.remaining >= 0.01 ? document.remaining : 0;
      };
      const depositRemaining = remainingOf(BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value);
      if (depositRemaining) notes.push("La facture d'acompte a été émise : il manque " + this.formatAmount(depositRemaining) + " à encaisser pour la régler.");
      const invoiceRemaining = remainingOf(BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.value);
      if (invoiceRemaining) notes.push("La facture a été émise : il manque " + this.formatAmount(invoiceRemaining) + " à encaisser pour la régler" + ((this.business.depositNumber && !isDepositAborted(this.business)) ? " (acompte déduit)." : "."));
      return notes;
    },
    // Alerte affichée section repliée quand il y a quelque chose à faire : créance à encaisser ou argent à demander
    attentionTitle() {
      const messages = [];
      if (this.summary.balance <= -0.01) messages.push("Reste à encaisser : " + this.formatAmount(Math.abs(this.summary.balance)));
      if (this.summary.pending >= 0.01) messages.push(this.formatAmount(this.summary.pending) + " restent à demander (document non émis)");
      else if (this.hasPending) messages.push("Les documents émis dépassent le total théorique du contrat");
      return messages.join(" · ");
    },
    sortedCollections() {
      return [...this.collections].sort((a, b) => new Date(b.date) - new Date(a.date));
    },
    isVisible() {
      return this.isLoaded && !!this.business
          && (!!this.business.invoiceNumber || !!this.business.depositNumber || this.collections.length > 0);
    },
  },
  async mounted() {
    await this.loadCollections();
  },
  methods: {
    convertPaymentType,
    async loadCollections() {
      if (!this.business || !this.business.id) return;
      // Tous les encaissements du contrat, sans paramètre de pagination (le controller ajoute déjà le « ? » de la query string)
      const resp = await CollectionController.getAllByBusiness(this.business.id, '');
      this.collections = (resp && !resp.errors) ? resp : [];
      this.isLoaded = true;
    },
    openAddCollectionModal() {
      this.$refs.addCollectionModal.openModal(this.business);
    },
    openEditCollectionModal(collection) {
      if (this.readOnly) return;
      this.$refs.editCollectionModal.openModal({...collection, businessId: collection.businessId || this.business.id});
    },
    // Après un ajout : le parent recharge le business (ce qui recrée la section) ; à défaut on recharge seulement les encaissements
    onCollectionAdded() {
      if (this.callback) this.callback();
      else this.loadCollections();
    },
    formatAmount(value) {
      return (Number(value) || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' €';
    },
    // Le solde est signé : + encaissé en trop, - reste à encaisser
    formatSignedAmount(value) {
      return (Number(value) || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'exceptZero'}) + ' €';
    },
    balanceCaption(balance) {
      if (balance > 0) return "Encaissé en trop";
      if (balance < 0) return "Reste à encaisser";
      return "Soldé";
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString("fr-FR", formattedDate);
    },
    purposeLabel(collection) {
      const purpose = Object.values(PAYMENT_PURPOSE).find(purpose => purpose.value === collection.paymentPurpose);
      if (!purpose) return "Non précisé";
      return collection.paymentPurpose === PAYMENT_PURPOSE.CREDIT_NOTE.value && collection.creditNoteNumber !== undefined
          ? `${purpose.label} AV${String(collection.creditNoteNumber).padStart(4, '0')}`
          : purpose.label;
    },
  },
};
</script>

<style lang="stylus" scoped>
.financial-alert
  color #b36b00

.add-collection-button
  margin-top 10px

// Même charte que « Nouveau devis » / « Nouveau client » : citron cerclé d'encre
.add-collection-button.is-highlighted :deep(button)
  background-color #FFDD2E
  border 2px solid #17233f
  color #17233f
  font-weight 700

.add-collection-button.is-highlighted :deep(button:hover)
  background-color #FFE95C
  border-color #17233f

.section-divider
  height 1px
  background-color #dde1e7
  margin 12px 0

.section-label
  margin 0 0 4px
  font-size 11.5px
  font-weight 800
  letter-spacing 0.05em
  text-transform uppercase
  color #3a4250

.collections-label
  margin-top 14px

.collections-toggle
  display flex
  align-items center
  gap 4px
  width 100%
  padding 0
  border 0
  background none
  font-family inherit
  text-align left
  &:not(:disabled)
    cursor pointer
    &:hover
      color #3d4450

.summary-row
  display flex
  align-items baseline
  justify-content space-between
  gap 8px
  padding 2px 0
  font-size 12px
  color #3d4450

  .summary-value
    font-weight 700
    color #30353b
    white-space nowrap

  // Solde calculé sur les documents émis seulement : présenté comme provisoire (même convention que les montants non figés)
  &.is-provisional
    .summary-value
      font-weight 500
      font-style italic
      color #566070

.summary-caption
  margin 0 0 4px
  font-size 11.5px
  color #566070
  text-align right

.summary-note
  display flex
  align-items flex-start
  gap 6px
  margin 6px 0 0
  padding 6px 8px
  border-radius 6px
  background-color #f1f3f6
  font-size 11.5px
  line-height 1.35
  color #3d4450

  .note-icon
    flex 0 0 auto
    padding-top 1px
    color #3a5a8f

.collection-list
  list-style none
  margin 0
  padding 0
  max-height 180px
  overflow-y auto

.collection-item
  display flex
  flex-direction column
  padding 5px 0
  border-bottom 1px solid #dde1e7
  &:last-child
    border-bottom 0

  &.editable
    cursor pointer

    &:hover
      background #f5f8fa

  .collection-main
    display flex
    justify-content space-between
    gap 8px
    font-size 12px
    color #3d4450

  .collection-amount
    font-weight 700
    color #30353b
    white-space nowrap

  .collection-meta
    font-size 11.5px
    color #566070
</style>